import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams, Routes, Route } from 'react-router-dom';
import { getProductBySlug } from '../data/products';
import { logPageView, logEvent, trackMetaEvent } from '../analytics';

function ProductModalContent() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProductBySlug(slug);

  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const close = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (product) {
      logPageView(`/product/${slug}`);
      logEvent('view_product', { product_id: product.id, product_name: product.name });
      trackMetaEvent('ViewContent', {
        content_name: product.name,
        content_ids: [product.id],
        content_type: 'product',
        value: product.price,
        currency: 'ILS',
      });
    }
  }, [slug, product]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [close]);

  if (!product) {
    return (
      <div className="pm-overlay" onClick={close}>
        <div className="pm-panel" onClick={(e) => e.stopPropagation()}>
          <p style={{ padding: '3rem', textAlign: 'center' }}>המוצר לא נמצא</p>
        </div>
      </div>
    );
  }

  const {
    id, name, nameHebrew, longDescription, price, image, intensity,
    tastingNotes, bestWith, roast, origin, preparation,
  } = product;

  function handleBuy() {
    logEvent(`click_buy_${id}`);
    trackMetaEvent('AddToCart', {
      content_name: name,
      content_ids: [id],
      content_type: 'product',
      value: price,
      currency: 'ILS',
    });
    setShowEmailInput(true);
  }

  async function handleSubmitEmail(e) {
    e.preventDefault();
    if (!email) return;

    logEvent(`lead_signup_${id}`, { label: email });
    trackMetaEvent('Lead', { content_name: name, content_ids: [id] });

    try {
      const formData = new URLSearchParams();
      formData.append('form-name', `notify-${id}`);
      formData.append('product-id', id);
      formData.append('email', email);

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch {
      // Netlify Forms only work on deployed site
    }

    setSubscribed(true);
  }

  const imgSrc = image ? `${process.env.PUBLIC_URL}${image}` : '';

  return (
    <div className="pm-overlay" onClick={close}>
      <div className="pm-panel" onClick={(e) => e.stopPropagation()}>
        <button className="pm-close" onClick={close} aria-label="סגור">✕</button>

        <div className="pm-hero">
          <div className="pm-image-wrap">
            <img src={imgSrc} alt={name} className="pm-image" />
          </div>

          <div className="pm-details">
            <h2 className="pm-title">{nameHebrew}</h2>
            <p className="pm-name-en">{name}</p>
            <p className="pm-long-desc">{longDescription}</p>

            {intensity && (
              <div className="product-intensity">
                <span className="intensity-label">עוצמה</span>
                <div className="intensity-dots">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <span
                      key={level}
                      className={`intensity-dot ${level <= intensity ? 'active' : ''}`}
                    />
                  ))}
                </div>
                <span className="intensity-value">{intensity}/5</span>
              </div>
            )}

            <p className="pm-price">₪{price}</p>

            <div className="product-actions">
              <button className="btn btn-primary" onClick={handleBuy}>
                קנייה מהירה
              </button>
              <button className="btn btn-promo" onClick={handleBuy}>
                3 ב-₪330 + משלוח חינם
              </button>
            </div>

            {showEmailInput && (
              <div className="pm-out-of-stock">
                <div className="modal-icon">🚢</div>
                <p className="modal-text">
                  אופס, המלאי אזל זמנית! המשלוח הטרי מגרמניה נמצא כרגע בלב ים
                  וצפוי להגיע בקרוב. שווה לחכות!
                </p>

                {!subscribed && (
                  <form className="notify-form" onSubmit={handleSubmitEmail}>
                    <input
                      type="email"
                      name="email"
                      className="email-input"
                      placeholder="הזינו אימייל"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <button type="submit" className="btn btn-accent">
                      עדכנו אותי כשהמלאי חוזר
                    </button>
                  </form>
                )}

                {subscribed && (
                  <p className="success-message">
                    נרשמת בהצלחה! נעדכן אותך ברגע שהפולים נוחתים בארץ.
                  </p>
                )}
              </div>
            )}

            <div className="pm-meta">
              <div className="pm-meta-item">
                <span className="pm-meta-label">קלייה</span>
                <span className="pm-meta-value">{roast}</span>
              </div>
              <div className="pm-meta-item">
                <span className="pm-meta-label">מקור</span>
                <span className="pm-meta-value">{origin}</span>
              </div>
              <div className="pm-meta-item">
                <span className="pm-meta-label">הכנה מומלצת</span>
                <span className="pm-meta-value">{preparation}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pm-sections">
          <div className="pm-section">
            <h3 className="pm-section-title">טעמים וארומות</h3>
            <div className="tasting-notes">
              {tastingNotes.map((note) => (
                <span key={note} className="tasting-note">{note}</span>
              ))}
            </div>
          </div>

          <div className="pm-section">
            <h3 className="pm-section-title">הכי טוב עם</h3>
            <ul className="best-with-list">
              {bestWith.map((item) => (
                <li key={item} className="best-with-item">{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductModal() {
  const location = useLocation();
  const isProduct = location.pathname.startsWith('/product/');

  if (!isProduct) return null;

  return (
    <Routes>
      <Route path="/product/:slug" element={<ProductModalContent />} />
    </Routes>
  );
}
